import React from 'react';
import { useActor } from '@xstate/react';

import { OtpFieldRefType, VERIFY_OTP_BUTTON_CLICKED } from '@nw/auth-core';
import {
  buttonSize,
  ButtonSize,
  ButtonVariant,
  buttonVariant
} from '@nw/design-system/omega';
import { OTP_LOADING } from '@nw/shared/config';

import { userAuthHandlerMachineEvents } from '../../constants/userAuthHandlerMachineConstants';
import {
  DoSelfTransition,
  UserAuthHandlerMachineSendType,
  UserAuthHandlerMachineStateType
} from '../../types/UserAuthHandlerMachineTypes';

import {
  VerifyAndSignUpText,
  VerifyOtpButtonContainer
} from './styledComponent';

interface VerifyOtpButtonProps {
  otpFieldRef: OtpFieldRefType;
  otp: string;
  buttonId: string;
  buttonText: string;
  isButtonLoading: boolean;
  isVerifyAndSignUpButtonDisabled: boolean;
  userAuthHandlerMachineSend: UserAuthHandlerMachineSendType;
  userAuthHandlerMachineState: UserAuthHandlerMachineStateType;
  renderButtonContent?: () => JSX.Element;
  shouldShrinkButtonWhileLoading?: boolean;
}

const VerifyOtpButton: React.FC<VerifyOtpButtonProps> = ({
  otpFieldRef,
  otp,
  buttonId,
  buttonText,
  isButtonLoading,
  isVerifyAndSignUpButtonDisabled,
  userAuthHandlerMachineSend,
  userAuthHandlerMachineState,
  renderButtonContent,
  shouldShrinkButtonWhileLoading = true
}) => {
  const [otpFieldState, otpFieldSend] = useActor(otpFieldRef);
  const { shouldDoSelfTransition } = userAuthHandlerMachineState.context;

  const isVerifyButtonLoading = () =>
    otpFieldState.tags.has(OTP_LOADING) || isButtonLoading;

  const onClickVerifyOtp = () => {
    if (!isVerifyButtonLoading() && !isVerifyAndSignUpButtonDisabled) {
      if (shouldDoSelfTransition) {
        userAuthHandlerMachineSend({
          type: userAuthHandlerMachineEvents.doSelfTransition
        } as DoSelfTransition);
      } else {
        otpFieldSend({
          type: VERIFY_OTP_BUTTON_CLICKED,
          values: { otp }
        } as any);
      }
    }
  };

  return (
    <VerifyOtpButtonContainer
      id={buttonId}
      isDisabled={isVerifyAndSignUpButtonDisabled}
      isLoading={isVerifyButtonLoading()}
      type={'submit'}
      variant={buttonVariant.primary as ButtonVariant}
      size={buttonSize.large as ButtonSize}
      onClick={onClickVerifyOtp}
      data-testid={'multi-step-verify-otp-button'}
      className={'verify-otp-button-container'}
      shouldShrinkButtonWhileLoading={shouldShrinkButtonWhileLoading}
    >
      {renderButtonContent ? (
        renderButtonContent()
      ) : (
        <VerifyAndSignUpText>{buttonText}</VerifyAndSignUpText>
      )}
    </VerifyOtpButtonContainer>
  );
};

export default VerifyOtpButton;
